import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getDocumentPath, DOCUMENT_NAMES, DocumentType } from '../config/legalDocuments';

const VALID_TYPES = ['tos', 'privacy', 'ai-notice', 'cookies', 'licenses'];

// Map URL slugs to config keys
const slugToConfigKey: Record<string, string> = {
    'tos': 'tos',
    'privacy': 'privacy',
    'ai-notice': 'ai_notice',
    'cookies': 'cookies',
    'licenses': 'licenses'
};

// Map URL slugs to display name keys
const slugToNameKey: Record<string, string> = {
    'tos': 'tos',
    'privacy': 'privacy',
    'ai-notice': 'ai_notice',
    'cookies': 'cookies',
    'licenses': 'licenses'
};

const LegalDocumentPage: React.FC = () => {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();
    const [documentContent, setDocumentContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const configKey = type ? slugToConfigKey[type] : undefined;
    const nameKey = type ? slugToNameKey[type] : undefined;
    const documentName = nameKey ? (DOCUMENT_NAMES[nameKey] || type) : '';

    useEffect(() => {
        const loadDocument = async () => {
            if (!type || !VALID_TYPES.includes(type) || !configKey) {
                setError('Neplatný typ dokumentu');
                setLoading(false);
                return;
            }

            try {
                const documentUrl = getDocumentPath(configKey);
                const response = await fetch(documentUrl);

                if (!response.ok) {
                    throw new Error('Dokument sa nepodarilo načítať');
                }

                const contentType = response.headers.get('content-type') || '';
                const content = await response.text();
                if (contentType.includes('text/html') || content.trimStart().startsWith('<!DOCTYPE')) {
                    throw new Error('Dokument sa nepodarilo načítať');
                }
                setDocumentContent(content);
            } catch (err) {
                console.error('Failed to load document:', err);
                setError('Dokument sa nepodarilo načítať. Skúste to znova.');
            } finally {
                setLoading(false);
            }
        };

        loadDocument();
    }, [type, configKey]);

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <button onClick={() => navigate(-1)} style={styles.backButton}>
                        &larr; Späť
                    </button>
                    <h1 style={styles.title}>{documentName}</h1>
                </div>
                <div style={styles.loadingContainer}>
                    <p>Načítavam dokument...</p>
                </div>
            </div>
        );
    }

    if (error || !documentContent) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <button onClick={() => navigate(-1)} style={styles.backButton}>
                        &larr; Späť
                    </button>
                </div>
                <div style={styles.errorContainer}>
                    <p style={styles.errorText}>{error || 'Dokument sa nepodarilo načítať'}</p>
                    <button onClick={() => navigate(-1)} style={styles.primaryButton}>
                        Späť
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <button onClick={() => navigate(-1)} style={styles.backButton}>
                    &larr; Späť
                </button>
                <h1 style={styles.title}>{documentName}</h1>
            </div>
            <div style={styles.documentContent} className="legal-document-markdown">
                <style>{`
                    .legal-document-markdown h1 { font-size: 24px; margin-top: 0; margin-bottom: 16px; color: #2c3e50; }
                    .legal-document-markdown h2 { font-size: 20px; margin-top: 28px; margin-bottom: 12px; color: #2c3e50; }
                    .legal-document-markdown h3 { font-size: 17px; margin-top: 20px; margin-bottom: 8px; color: #34495e; }
                    .legal-document-markdown p { margin-bottom: 12px; line-height: 1.7; color: #34495e; }
                    .legal-document-markdown ul, .legal-document-markdown ol { margin-bottom: 12px; padding-left: 24px; }
                    .legal-document-markdown li { margin-bottom: 6px; line-height: 1.6; color: #34495e; }
                    .legal-document-markdown table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
                    .legal-document-markdown th, .legal-document-markdown td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 14px; }
                    .legal-document-markdown th { background-color: #f8f9fa; font-weight: 600; }
                    .legal-document-markdown strong { font-weight: 600; }
                    .legal-document-markdown a { color: #3498db; }
                    .legal-document-markdown pre { background: rgba(0,0,0,0.03); padding: 12px; border-radius: 5px; overflow-x: auto; font-size: 12px; border: 1px solid #e5e7eb; }
                    .legal-document-markdown code { font-size: 13px; }
                `}</style>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{documentContent}</ReactMarkdown>
            </div>
            <div style={styles.footer}>
                <button onClick={() => navigate(-1)} style={styles.secondaryButton}>
                    Späť
                </button>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        minHeight: '100vh',
        paddingBottom: '140px',
    },
    header: {
        backgroundColor: '#fff',
        padding: '15px 20px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        maxWidth: '700px',
        margin: '0 auto',
    },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#3498db',
        fontSize: '16px',
        cursor: 'pointer',
        padding: '8px 0',
        marginBottom: '10px',
        display: 'inline-block',
    },
    title: {
        margin: 0,
        fontSize: '20px',
        fontWeight: 600,
        color: '#2c3e50',
    },
    documentContent: {
        maxWidth: '700px',
        margin: '0 auto',
        padding: '20px',
        lineHeight: '1.6',
    },
    footer: {
        padding: '20px',
        textAlign: 'center',
    },
    secondaryButton: {
        backgroundColor: '#95a5a6',
        color: '#fff',
        border: 'none',
        padding: '12px 30px',
        fontSize: '15px',
        fontWeight: 600,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    loadingContainer: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#7f8c8d',
    },
    errorContainer: {
        textAlign: 'center',
        padding: '60px 20px',
    },
    errorText: {
        color: '#e74c3c',
        fontSize: '16px',
        marginBottom: '20px',
    },
    primaryButton: {
        backgroundColor: '#3498db',
        color: '#fff',
        border: 'none',
        padding: '12px 30px',
        fontSize: '15px',
        fontWeight: 600,
        borderRadius: '8px',
        cursor: 'pointer',
    },
};

export default LegalDocumentPage;
